import { Button, Field, Modal } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { useAppDispatch, useAppSelector } from "@/App"
import { selectClientById, updateClient } from "@/store/clients"
import { Client } from "@/types"
import { fetchSetTemporaryEndTime } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import cls from "../ClientContextMenu.module.scss"

type UpdateWorkingHoursModalProps = {
  clientId: Client["id"]
  handleClose: () => void
}

export const UpdateWorkingHoursModal = ({ clientId, handleClose }: UpdateWorkingHoursModalProps) => {
  const client = useAppSelector(selectClientById(clientId))

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [workingHoursEnd, setWorkingHoursEnd] = useState(dayjs().format("HH:mm"))

  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  if (!client) return null

  const handleChangeWorkingHoursEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkingHoursEnd(e.target.value)
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    setIsSubmitting(true)

    try {
      const { response, json } = await fetchSetTemporaryEndTime({ clientId, body: { time: workingHoursEnd } })
      const updatedClient = json?.data

      if (response.ok && updatedClient) {
        toast.success(t("clients:temporary_end_time_success"))
        dispatch(updateClient(updatedClient))

        handleClose()
      } else {
        handleStandardError(response, json)
      }
    } catch (error) {
      toast.error(t("clients:temporary_end_time_fail"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      handleClose={handleClose}
      className={clsx(cls.distanceLimitModal, cls.workingHoursModal)}
      closeBtnPosition="inside"
    >
      <Modal.Title>
        <div className="h300">Lokalizacja: {client.name}</div>
        <div className="h200 mt-2.5">
          Godziny pracy: {client.start_working_hours} - {client.end_working_hours}
          {client.temporary_end_working_hours && <span className="ml-1">({client.temporary_end_working_hours})</span>}
        </div>
      </Modal.Title>

      <Modal.Content>
        <form onSubmit={handleSubmit}>
          <Field
            className="mt-2 mb-2"
            label={t("clients:extend_working_hours")}
            name="end_working_hours"
            inputProps={{
              id: "end_working_hours",
              value: workingHoursEnd,
              onChange: handleChangeWorkingHoursEnd,
              type: "time",
              plain: true,
              required: true,
              min: dayjs().format("HH:mm"),
              autoFocus: true,
            }}
          />

          <div className={cls.actions}>
            <Button onClick={handleClose} color="muted" variation="outlined">
              Anuluj
            </Button>

            <Button color="primary" type="submit" raised disabled={isSubmitting}>
              Potwierdź
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal>
  )
}
